import "dayjs/locale/pt-br";
import { useEffect, useState } from "react";

import { Autocomplete, Icon, LinearProgress, TextField } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import RecebimentosPorTipoPay from "./components/RecebimentosPorTipoPay";
import RecebimentosPorCategoriaIdade from "./components/RecebimentosPorCategoriaIdade";
import GraficoInscricoes from "./components/GraficoInscricoes";
import Adiantamentos from "./components/Adiantamentos";
import DespesasEventoRelatorio from "./components/DespesasEventoRelatorio";

import { setMessage, useMaterialUIController } from "context";

import { IEventoResponse } from "interfaces/eventos";
import { IReports, Pagamentos } from "interfaces/reports";

import {
  EventosProps,
  getAllEventos,
  getAllEventosPeridoByActive,
} from "services/evento";
import { getReportInscricao } from "services/reports";

import { formatDate, formatDateEndOfDay } from "helpers/formatDate";
import generatePDF from "helpers/generatePDF";
import { getImageBase64 } from "helpers/getImageBase64";
import { formatCurrency } from "helpers/utils";

import logoTNV from "../../../../assets/images/logo-tnv.png";
import {
  buildAdiantamentosPdfContent,
  buildDespesasPdfContent,
  buildResumoFinanceiroPdfContent,
  buildTaxasBancariasPdfContent,
  exportFinanceiroEventoExcel,
  montarResumoFinanceiroEvento,
} from "./helpers/export-financeiro-evento.helpers";
import { listarTodasDespesasEvento } from "./helpers/listar-todas-despesas-evento";

export type totalsType = {
  totalGeralInscricoes: { qtd: number; valor: number };
  totalPix: { qtd: number; valor: number };
  totalCartao: { qtd: number; valor: number };
  totalBoleto: { qtd: number; valor: number };
  totalDesconto: { qtd: number; valor: number };
  totalCortesia: { qtd: number; valor: number };
  totalCancelado: { qtd: number; valor: number };
  totalArrecadado: { qtd: number; valor: number };
  totalTaxaAdministrativa: { qtd: number; valor: number };
  totalValorPendentes: { qtd: number; valor: number };
  totalParticipantes: { qtd: number };
  totalParticipantesCortesia: { qtd: number };
  totalParticipantesPendentes: { qtd: number };
  categorias: Categoria[];
  taxaBancaria: {
    pix: { taxa: string; total: number };
    cartao: { taxa: string; total: number };
    boleto: { taxa: string; total: number };
  };
};

function Analytics(): JSX.Element {
  const [, dispatch] = useMaterialUIController();
  const [isloading, setIsLoading] = useState<boolean>(true);
  const [eventos, setEventos] = useState<EventosProps>({
    eventos: [],
    total: 0,
    currentPage: 0,
    totalPages: 0,
  });
  const [selectedEvento, setSelectedEvento] = useState<IEventoResponse | null>(
    null
  );
  const [report, setReport] = useState<IReports[]>(null);
  const [totals, setTotals] = useState<totalsType>(null);

  useEffect(() => {
    (async () => {
      const result = await getAllEventosPeridoByActive().finally(() =>
        setIsLoading(false)
      );
      // Lista de eventos ativos
      setEventos(result);

      // Selecionar o primeiro evento
      await handlerSelectEvento(result.eventos[0]);
    })();
  }, []);

  //Carregar todos os eventos
  const handlerListAllEventos = async () => {
    try {
      setIsLoading(true);
      const result = await getAllEventos(1, 100, "all");
      setEventos(result);
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error?.message || "Erro ao carregar todos os eventos",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlerSelectEvento = async (evento: IEventoResponse) => {
    try {
      setIsLoading(true);
      setSelectedEvento(evento);
      // Buscar todas inscrições do evento e os pagamentos
      const { data, status } = await getReportInscricao(evento.id);

      if (status === 200) {
        const result = await calcularRelatorio(evento, data);
        setTotals(result);
        setReport(data); // Buscar todas inscrições do evento e os pagamentos
      }
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: "Selecione um evento",
        severity: "warning",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calcularTaxasBancarias = (totalsData: totalsType) =>
    Number(totalsData.taxaBancaria.pix.total) +
    Number(totalsData.taxaBancaria.cartao.total) +
    Number(totalsData.taxaBancaria.boleto.total);

  const handleImprimirPdf = async () => {
    if (!selectedEvento || !totals) return;

    setIsLoading(true);
    let despesasEvento: Awaited<ReturnType<typeof listarTodasDespesasEvento>> =
      [];
    try {
      despesasEvento = await listarTodasDespesasEvento(selectedEvento.id);
    } catch {
      despesasEvento = [];
    }

    const base64Image = await getImageBase64(logoTNV);

    const totalTaxasBancarias = calcularTaxasBancarias(totals);
    const listaAdiantamentos = selectedEvento.adiantamentoEvento ?? [];
    const resumoFinanceiro = montarResumoFinanceiroEvento({
      totals,
      adiantamentos: listaAdiantamentos,
      despesas: despesasEvento,
      valorServico: Number(selectedEvento.valorServico) || 0,
      taxasBancarias: totalTaxasBancarias,
    });

    await generatePDF({
      filename: `Financeiro - ${selectedEvento?.slug.toLowerCase()}.pdf`,
      header: {
        text: "RELATÓRIO FINANCEIRO",
        alignment: "center",
        margin: [40, 40, 40, 40],
        bold: true,
        fontSize: 16,
      },
      content: [
        {
          layout: "noBorders",
          margin: [0, 10, 0, 10],
          table: {
            widths: ["auto", "*"],
            body: [
              [
                { image: base64Image, fit: [80, 80] },
                {
                  text: [
                    {
                      text: `${selectedEvento?.titulo.toUpperCase()}\n`,
                      bold: true,
                      fontSize: 14,
                    },
                    {
                      text: `Data: ${formatDateEndOfDay(selectedEvento?.dataEvento)} - `,
                      fontSize: 12,
                    },
                    {
                      text: `${selectedEvento?.modalidade.tipo}\n`,
                      fontSize: 12,
                    },
                    {
                      text: `Organizador: ${selectedEvento?.organizador.nomeFantasia}\n`,
                      fontSize: 12,
                    },
                  ],
                },
              ],
            ],
          },
        },
        {
          fontSize: 11,
          table: {
            widths: ["*", "*", "*", "*", "*", "*"],
            heights: [20, 20, 20],
            body: [
              [
                {
                  text: "Total Arrecadado",
                  alignment: "center",
                  bold: true,
                  fillColor: "#CCCCCC",
                },
                {
                  text: "Boleto",
                  alignment: "center",
                  bold: true,
                  fillColor: "#CCCCCC",
                },
                {
                  text: "Pix",
                  alignment: "center",
                  bold: true,
                  fillColor: "#CCCCCC",
                },
                {
                  text: "Cartão de Crédito",
                  alignment: "center",
                  bold: true,
                  fillColor: "#CCCCCC",
                },
                {
                  text: "Cortesia",
                  alignment: "center",
                  bold: true,
                  fillColor: "#CCCCCC",
                },
                {
                  text: "Descontos",
                  alignment: "center",
                  bold: true,
                  fillColor: "#CCCCCC",
                },
              ],
              [
                {
                  text: `${formatCurrency(totals.totalArrecadado.valor)}`,
                  alignment: "right",
                  margin: [0, 5, 0, 5],
                },
                {
                  text: `${formatCurrency(totals.totalBoleto.valor)}`,
                  alignment: "right",
                  margin: [0, 5, 0, 5],
                },
                {
                  text: `${formatCurrency(totals.totalPix.valor)}`,
                  alignment: "right",
                  margin: [0, 5, 0, 5],
                },
                {
                  text: `${formatCurrency(totals.totalCartao.valor)}`,
                  alignment: "right",
                  margin: [0, 5, 0, 5],
                },
                {
                  text: `${formatCurrency(totals.totalCortesia.valor)}`,
                  alignment: "right",
                  margin: [0, 5, 0, 5],
                },
                {
                  text: `${formatCurrency(totals.totalDesconto.valor)}`,
                  alignment: "right",
                  margin: [0, 5, 0, 5],
                },
              ],
              [
                {
                  text: `Qtd insc.: ${totals.totalArrecadado.qtd}`,
                  alignment: "center",
                  margin: [0, 5, 0, 5],
                  fontSize: 9,
                },
                {
                  text: `Qtd insc.: ${totals.totalBoleto.qtd}`,
                  alignment: "center",
                  margin: [0, 5, 0, 5],
                  fontSize: 9,
                },
                {
                  text: `Qtd insc.: ${totals.totalPix.qtd}`,
                  alignment: "center",
                  margin: [0, 5, 0, 5],
                  fontSize: 9,
                },
                {
                  text: `Qtd insc.: ${totals.totalCartao.qtd}`,
                  alignment: "center",
                  margin: [0, 5, 0, 5],
                  fontSize: 9,
                },
                {
                  text: `Qtd insc.: ${totals.totalCortesia.qtd}`,
                  alignment: "center",
                  margin: [0, 5, 0, 5],
                  fontSize: 9,
                },
                {
                  text: `Qtd insc.: ${totals.totalDesconto.qtd}`,
                  alignment: "center",
                  margin: [0, 5, 0, 5],
                  fontSize: 9,
                },
              ],
            ],
          },
        },
        ...buildResumoFinanceiroPdfContent(resumoFinanceiro, {
          totalInscricoes: totals.totalGeralInscricoes.qtd,
          inscricoesPagas: totals.totalArrecadado.qtd,
          inscricoesCanceladas: totals.totalCancelado.qtd,
          totalAtletas: totals.totalParticipantes.qtd,
          atletasCortesia: totals.totalParticipantesCortesia.qtd,
          taxaInscricaoUnitaria: Number(selectedEvento.taxaInscricao) || 0,
        }),
        ...buildTaxasBancariasPdfContent(
          totals.taxaBancaria,
          totalTaxasBancarias,
        ),
        ...buildAdiantamentosPdfContent(listaAdiantamentos),
        ...buildDespesasPdfContent(despesasEvento),
      ],
      footer: {
        columns: [
          {
            text: "https://www.tnvteam.com.br/",
            alignment: "left",
            margin: [40, 0, 0, 0],
            fontSize: 9,
          },
          {
            text: `${formatDate(new Date(), "dd/MM/yyyy HH:mm:ss")}`,
            alignment: "right",
            margin: [0, 0, 40, 0],
            fontSize: 9,
          },
        ],
      },
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handlerImprmirExcel = async () => {
    if (!selectedEvento || !totals || !report) return;

    setIsLoading(true);
    let despesasEvento: Awaited<ReturnType<typeof listarTodasDespesasEvento>> =
      [];
    try {
      despesasEvento = await listarTodasDespesasEvento(selectedEvento.id);
    } catch {
      despesasEvento = [];
    }

    const filterAtleta: any[] = [];

    report.forEach((element) => {
      for (let atleta of element.inscritos) {
        if (element.statusInscricao.tipo === "Pago") {
          filterAtleta.push({
            ninsc: element.numeroInscricao,
            nome: atleta.atleta.nome,
            dataNascimento: formatDateEndOfDay(atleta.atleta.dataNascimento),
            sexo: atleta.atleta.sexo.tipo.charAt(0),
            equipe: atleta.atleta.equipe,
            cidade: `${
              atleta.atleta.enderecos[0].cidade
            }/${atleta.atleta.enderecos[0].uf.toUpperCase()}`,
            categoria: atleta.categoria.descricao,
            desconto: Number(atleta.valorDesconto),
            taxa: Number(element.taxa),
            valorInscricao: Number(atleta.valorInscricao),
            totalInscricao:
              Number(atleta.valorInscricao) +
              Number(element.taxa) -
              Number(atleta.valorDesconto),
            dataInscricao: formatDate(element.dataInscricao, "dd/MM/yyyy HH:mm:ss"),
          });
        }
      }
    });

    try {
      const totalTaxasBancarias = calcularTaxasBancarias(totals);
      const listaAdiantamentos = selectedEvento.adiantamentoEvento ?? [];

      exportFinanceiroEventoExcel({
        tituloEvento: selectedEvento.titulo,
        inscritos: filterAtleta,
        despesas: despesasEvento,
        adiantamentos: listaAdiantamentos,
        resumoFinanceiro: montarResumoFinanceiroEvento({
          totals,
          adiantamentos: listaAdiantamentos,
          despesas: despesasEvento,
          valorServico: Number(selectedEvento.valorServico) || 0,
          taxasBancarias: totalTaxasBancarias,
        }),
        totaisResumo: [
          {
            description: "Total arrecadado",
            value: formatCurrency(totals.totalArrecadado.valor),
          },
        ],
        filename: `Relatório inscrições Atleta ${selectedEvento.titulo}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {/* navbar */}
      <DashboardNavbar />

      {/* content */}
      <MDBox my={2} display="flex" justifyItems="center">
        <MDBox
          position="relative"
          width="100%"
          bgColor="white"
          p={3}
          borderRadius="lg"
          shadow="sm"
        >
          <MDBox display="flex" flexWrap="wrap" gap={2}>
            <MDBox display="flex" flexGrow="2">
              <Autocomplete
                id="eventos-select"
                value={selectedEvento}
                fullWidth
                options={eventos.eventos}
                getOptionLabel={(option) =>
                  `${option.id.toString().padStart(4, "0")} | ${formatDateEndOfDay(
                    option.dataEvento
                  )} | ${option.titulo} | ${
                    option.endereco[0].cidade
                  }-${option.endereco[0].uf.toUpperCase()}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handlerSelectEvento(value)}
                noOptionsText="Nenhum evento encontrado"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione o evento"
                    sx={{ height: "100%" }}
                    InputLabelProps={{
                      style: { lineHeight: "1.5em" },
                    }}
                  />
                )}
              />
            </MDBox>

            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="row"
              width={{ xs: "100%", sm: "auto" }}
              gap={1}
            >
              <MDButton
                variant="gradient"
                color="light"
                fullWidth
                onClick={handlerListAllEventos}
              >
                Todos eventos
              </MDButton>
              <MDButton
                onClick={handleImprimirPdf}
                variant="outlined"
                color="dark"
                size="medium"
                sx={{ gap: 1 }}
              >
                <Icon>print</Icon>PDF
              </MDButton>
              <MDButton
                onClick={handlerImprmirExcel}
                variant="outlined"
                color="dark"
                size="medium"
                sx={{ gap: 1 }}
              >
                <Icon>print</Icon>Excel
              </MDButton>
            </MDBox>
          </MDBox>
          {isloading && (
            <MDBox
              position="absolute"
              left={0}
              bottom={0}
              borderRadius="lg"
              width="100%"
              pt={2}
              sx={{ width: "100%", overflow: "hidden" }}
            >
              <LinearProgress />
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <RecebimentosPorTipoPay evento={selectedEvento} totals={totals} />

      <RecebimentosPorCategoriaIdade totals={totals} evento={selectedEvento} />

      <GraficoInscricoes evento={selectedEvento} report={report} />

      <Adiantamentos adiantamentos={selectedEvento?.adiantamentoEvento || []} />

      {selectedEvento ? (
        <DespesasEventoRelatorio
          key={selectedEvento.id}
          eventoId={selectedEvento.id}
        />
      ) : null}

      {/* footer */}
      <Footer />
    </DashboardLayout>
  );
}

type Categoria = {
  qtd: number;
  categoria: string;
  totalInsc: number;
};

// FUNÇÃO AUXILIAR CALCULAR VALORES
async function calcularRelatorio(evento: IEventoResponse, report: IReports[]) {
  const valorTaxaAdministrativa = Number(evento.taxaInscricao) || 0;

  let localTotalGeralInscricoes = { qtd: 0, valor: 0 };
  let localTotalPix = { qtd: 0, valor: 0 };
  let localTotalCartao = { qtd: 0, valor: 0 };
  let localTotalBoleto = { qtd: 0, valor: 0 };
  let localTotalDesconto = { qtd: 0, valor: 0 };
  let localTotalCortesia = { qtd: 0, valor: 0 };
  let localQtdAtletasCortesia = { qtd: 0 };
  let localTotalCancelado = { qtd: 0, valor: 0 };
  let localTotalArrecadado = { qtd: 0, valor: 0 };
  let localTaxaAdministrativa = { qtd: 0, valor: 0 };
  let localTotalValorPendentes = { qtd: 0, valor: 0 };
  let localTotalParticipantes = { qtd: 0 };
  let localTotalParticipantesPendentes = { qtd: 0 };
  let localTotalTaxaPix = { qtd: 0, valor: 0, taxa: 0 };
  let localTotalTaxaCartao = { qtd: 0, valor: 0, taxa: 0 };
  let localTotalTaxaBoleto = { qtd: 0, valor: 0, taxa: 0 };

  let categorias: Categoria[] = [];

  const isGratuito = report.some(
    (item) => item.statusInscricao.tipo === "Gratuito"
  );

  async function calcularTaxaBancaria(pagamento: Pagamentos): Promise<void> {
    if (pagamento.tipoPagamento.tipoPagamento === "Pix") {
      localTotalTaxaPix = {
        qtd: localTotalTaxaPix.qtd + 1,
        taxa: Number(pagamento.taxaBancaria),
        valor:
          localTotalTaxaPix.valor +
          (Number(pagamento.taxaBancaria) / 100) * Number(pagamento.total),
      };
    }
    if (pagamento.tipoPagamento.tipoPagamento === "Boleto") {
      localTotalTaxaBoleto = {
        qtd: localTotalTaxaBoleto.qtd + 1,
        taxa: Number(pagamento.taxaBancaria),
        valor: localTotalTaxaBoleto.valor + Number(pagamento.taxaBancaria),
      };
    }
    if (pagamento.tipoPagamento.tipoPagamento === "Cartão Crédito") {
      localTotalTaxaCartao = {
        qtd: localTotalTaxaCartao.qtd + 1,
        taxa: Number(pagamento.taxaBancaria),
        valor:
          localTotalTaxaCartao.valor +
          (Number(pagamento.taxaBancaria) / 100) * Number(pagamento.total),
      };
    }
  }

  if (isGratuito) {
    report.forEach((item) => {
      // Somar as categorias quantidade e totalInscricao
      item.inscritos.forEach((atletaInscritos) => {
        const categoriaExistente = categorias.find(
          (cat) => cat.categoria === atletaInscritos.categoria.descricao
        );
        if (categoriaExistente) {
          categoriaExistente.qtd += 1;
          categoriaExistente.totalInsc = 0;
        } else {
          categorias.push({
            qtd: 1,
            categoria: atletaInscritos.categoria.descricao,
            totalInsc: 0,
          });
        }
      });
    });
  }

  if (!isGratuito || !report) {
    report.forEach((item) => {
      // Somar total geral inscrições
      localTotalGeralInscricoes = {
        qtd: localTotalGeralInscricoes.qtd + 1,
        valor: localTotalGeralInscricoes.valor + Number(item.totalInscricao),
      };

      switch (item.statusInscricao.tipo) {
        case "Pago":
          localTotalArrecadado = {
            qtd: localTotalArrecadado.qtd + 1,
            valor: localTotalArrecadado.valor + Number(item.totalInscricao),
          };
          localTaxaAdministrativa = {
            qtd: localTaxaAdministrativa.qtd + 1,
            valor: localTaxaAdministrativa.valor + Number(item.totalTaxa),
          };

          calcularTaxaBancaria(item.pagamentos);

          // Total de descontos
          Number(item.totalDesconto) > 0 &&
            (localTotalDesconto = {
              qtd: localTotalDesconto.qtd + 1,
              valor: localTotalDesconto.valor + Number(item.totalDesconto),
            });
          // Total pagamentos PIX
          item.pagamentos?.cobrancaPix &&
            (localTotalPix = {
              qtd: localTotalPix.qtd + 1,
              valor: localTotalPix.valor + Number(item.totalInscricao),
            });
          //Total pagamentos BOLETO/CARTÃO
          if (item.pagamentos?.cobrancaBoleto) {
            if (item.pagamentos.cobrancaBoleto?.card_mask) {
              localTotalCartao = {
                qtd: localTotalCartao.qtd + 1,
                valor: localTotalCartao.valor + Number(item.totalInscricao),
              };
            } else {
              localTotalBoleto = {
                qtd: localTotalBoleto.qtd + 1,
                valor: localTotalBoleto.valor + Number(item.totalInscricao),
              };
            }
          }

          // Total de participantes
          localTotalParticipantes = {
            qtd: localTotalParticipantes.qtd + item.inscritos.length,
          };

          //-----------------------------------------------------
          // Somar as categorias quantidade e totalInscricao
          //-----------------------------------------------------
          item.inscritos.forEach((atletaInscritos) => {
            const categoriaExistente = categorias.find(
              (cat) => cat.categoria === atletaInscritos.categoria.descricao
            );

            const totalInscricao =
              Number(atletaInscritos.valorInscricao) +
              valorTaxaAdministrativa -
              Number(atletaInscritos.valorDesconto);

            if (categoriaExistente) {
              categoriaExistente.qtd += 1;
              categoriaExistente.totalInsc += totalInscricao;
            } else {
              categorias.push({
                qtd: 1,
                categoria: atletaInscritos.categoria.descricao,
                totalInsc: totalInscricao,
              });
            }
          });
          break;
        case "Cancelado":
          localTotalCancelado = {
            qtd: localTotalCancelado.qtd + 1,
            valor:
              localTotalCancelado.valor +
              (item.pagamentos?.dataBaixa ? Number(item.totalInscricao) : 0),
          };
          break;
        case "Cortesia": // Quatidade de inscrições fornecidas como cortesia
          localTotalCortesia = {
            qtd: localTotalCortesia.qtd + 1,
            valor: localTotalCortesia.valor + Number(item.totalInscricao),
          };
          // Quantidade de atletas inscritos como cortesia
          localQtdAtletasCortesia = {
            qtd: localQtdAtletasCortesia.qtd + item.inscritos.length,
          };
          break;
        case "Pendente":
          localTotalValorPendentes = {
            qtd: localTotalValorPendentes.qtd + 1,
            valor: localTotalValorPendentes.valor + Number(item.totalInscricao),
          };
          localTotalParticipantesPendentes = {
            qtd: localTotalParticipantesPendentes.qtd + item.inscritos.length,
          };
          break;
        default:
          break;
      }
    });
  }

  // Ordenar o Array em ordem de idade crescente
  categorias.sort((a, b) => a.categoria.localeCompare(b.categoria));

  const data: totalsType = {
    totalGeralInscricoes: localTotalGeralInscricoes,
    totalArrecadado: localTotalArrecadado,
    totalPix: localTotalPix,
    totalCartao: localTotalCartao,
    totalBoleto: localTotalBoleto,
    totalDesconto: localTotalDesconto,
    totalCortesia: localTotalCortesia,
    totalCancelado: localTotalCancelado,
    totalTaxaAdministrativa: {
      qtd: localTaxaAdministrativa.qtd - localTotalCortesia.qtd,
      valor: localTaxaAdministrativa.valor,
    },
    totalValorPendentes: localTotalValorPendentes,
    totalParticipantes: {
      qtd: localTotalParticipantes.qtd + localQtdAtletasCortesia.qtd,
    },
    totalParticipantesCortesia: localQtdAtletasCortesia,
    totalParticipantesPendentes: localTotalParticipantesPendentes,
    categorias: categorias,
    taxaBancaria: {
      pix: {
        taxa: localTotalTaxaPix.taxa.toString(),
        total: localTotalTaxaPix.valor,
      },
      boleto: {
        taxa: localTotalTaxaBoleto.taxa.toString(),
        total: localTotalTaxaBoleto.valor,
      },
      cartao: {
        taxa: localTotalTaxaCartao.taxa.toString(),
        total: localTotalTaxaCartao.valor,
      },
    },
  };

  return data;
}

export default Analytics;
